import { View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text } from "@src/components";
import type { VehicleClass } from "@visioni/a3s-vehicle-tracker";

const LABELS: Record<VehicleClass, string> = { light: "Light", heavy: "Heavy" };

interface VehicleClassIndicatorProps {
  vehicleClass: VehicleClass | null;
}

/** "○ Light" / "○ Heavy", or a muted "Not sure" when the body type has no class. */
export function VehicleClassIndicator({ vehicleClass }: VehicleClassIndicatorProps) {
  const { theme } = useUnistyles();
  // Product colours live here, not in the shared theme. Dark mode uses lighter
  // shades so the rings keep contrast on dark cards (blue 500 is 2.5:1 there).
  // Picked here rather than in StyleSheet.create: unistyles' compiler doesn't
  // reliably track a `currentTheme` ternary inside a stylesheet factory.
  const dark = theme.currentTheme === "dark";
  const ringColors: Record<VehicleClass, string> = {
    light: dark ? theme.colors.blue[400] : theme.colors.blue[500],
    heavy: dark ? theme.colors.warning[500] : theme.colors.warning[600],
  };

  if (vehicleClass === null) {
    return (
      <Text textStyle="sm/regular" color="textMuted">
        Not sure
      </Text>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.ring, { borderColor: ringColors[vehicleClass] }]} />
      <Text textStyle="sm/regular" color="text">
        {LABELS[vehicleClass]}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  ring: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
  },
}));
